import React, { useEffect, useMemo, useState } from 'react';
import {
    Sparkles,
    Send,
    Search,
    Loader2,
    Building2,
    User,
    Star,
    MapPin,
    Briefcase,
    GraduationCap,
    Check,
    X,
    Bot,
    ChevronDown,
    MessageCircle,
} from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import {
    fetchJobsForTargeting,
    fetchCompanyById,
    runJobBasedDraft,
    sendAsDavidToCompany,
    listDavidCompanyConversations,
    getDavidCompanyMessages,
    draftCandidateToRecommendation,
    DraftResponse,
    DraftCandidate,
    JobSummary,
    CompanyDetail,
    DavidCompanyConversation,
    DavidCompanyMessage,
} from '../services/davidInboxApi';

// ────────────────────────────────────────────────────────────────────────────
// DavidInbox — the CEO's workspace for company-side David messaging.
//
// Flow: pick a JOB → David finds candidates that match it → review rich
// signals → edit the drafted reply → send to the job's owning company.
//
// The target company is derived from the selected job's companyId — no
// separate company picker. This keeps the draft grounded in an actual job
// posting and reuses the hybrid retrieval pipeline on matches-vectors
// `POST /admin/v1/jobs/{jobId}/find-candidates`.
// ────────────────────────────────────────────────────────────────────────────

type ViewMode = 'compose' | 'history';

const DavidInbox: React.FC = () => {
    const [viewMode, setViewMode] = useState<ViewMode>('compose');

    // ── Job picker state ────────────────────────────────────────────────────
    const [jobs, setJobs] = useState<JobSummary[]>([]);
    const [jobsLoading, setJobsLoading] = useState(false);
    const [selectedJobId, setSelectedJobId] = useState<string>('');
    const [jobSearch, setJobSearch] = useState('');

    // ── Target company (resolved from selected job) ─────────────────────────
    const [resolvedCompany, setResolvedCompany] = useState<CompanyDetail | null>(null);
    const [resolvingCompany, setResolvingCompany] = useState(false);

    // ── Draft state ─────────────────────────────────────────────────────────
    const [draft, setDraft] = useState<DraftResponse | null>(null);
    const [draftLoading, setDraftLoading] = useState(false);
    const [draftError, setDraftError] = useState<string | null>(null);
    const [editedDraftText, setEditedDraftText] = useState('');

    // ── Send state ──────────────────────────────────────────────────────────
    const [sending, setSending] = useState(false);
    const [sendError, setSendError] = useState<string | null>(null);
    const [sendSuccess, setSendSuccess] = useState<string | null>(null);

    // ── History state ───────────────────────────────────────────────────────
    const [history, setHistory] = useState<DavidCompanyConversation[]>([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
    const [activeMessages, setActiveMessages] = useState<DavidCompanyMessage[]>([]);
    const [activeMessagesLoading, setActiveMessagesLoading] = useState(false);

    // Load jobs once on mount
    useEffect(() => {
        setJobsLoading(true);
        fetchJobsForTargeting()
            .then((items) => setJobs(items))
            .catch((err) => console.error('[DavidInbox] Failed to load jobs', err))
            .finally(() => setJobsLoading(false));
    }, []);

    // When a job is selected, resolve its company + owner
    useEffect(() => {
        if (!selectedJobId) {
            setResolvedCompany(null);
            return;
        }
        const job = jobs.find((j) => j.id === selectedJobId);
        if (!job?.companyId) {
            setResolvedCompany(null);
            return;
        }
        setResolvingCompany(true);
        fetchCompanyById(job.companyId)
            .then((c) => setResolvedCompany(c))
            .finally(() => setResolvingCompany(false));
    }, [selectedJobId, jobs]);

    // Load history when switching to history view
    useEffect(() => {
        if (viewMode !== 'history') return;
        setHistoryLoading(true);
        listDavidCompanyConversations()
            .then((items) => setHistory(items))
            .catch((err) => console.error('[DavidInbox] Failed to load history', err))
            .finally(() => setHistoryLoading(false));
    }, [viewMode]);

    // Load messages when a past conversation is clicked
    useEffect(() => {
        if (!activeConversationId) return;
        setActiveMessagesLoading(true);
        getDavidCompanyMessages(activeConversationId)
            .then((msgs) => setActiveMessages(msgs))
            .catch((err) => console.error('[DavidInbox] Failed to load messages', err))
            .finally(() => setActiveMessagesLoading(false));
    }, [activeConversationId]);

    const selectedJob = useMemo(
        () => jobs.find((j) => j.id === selectedJobId),
        [jobs, selectedJobId]
    );

    const filteredJobs = useMemo(() => {
        if (!jobSearch.trim()) return jobs;
        const q = jobSearch.trim().toLowerCase();
        return jobs.filter(
            (j) =>
                j.title.toLowerCase().includes(q) ||
                j.companyName.toLowerCase().includes(q) ||
                j.location?.toLowerCase().includes(q)
        );
    }, [jobs, jobSearch]);

    // ── Handlers ────────────────────────────────────────────────────────────

    const handleRunDraft = async () => {
        if (!selectedJob) {
            setDraftError('Pick a job first');
            return;
        }
        setDraftLoading(true);
        setDraftError(null);
        setSendSuccess(null);
        try {
            // Ensure company is resolved (may still be in-flight)
            let company = resolvedCompany;
            if (!company && selectedJob.companyId) {
                company = await fetchCompanyById(selectedJob.companyId);
                setResolvedCompany(company);
            }
            const result = await runJobBasedDraft(selectedJob, company, 5);
            setDraft(result);
            setEditedDraftText(result.draft || '');
        } catch (err: any) {
            setDraftError(err?.message || 'Failed to generate draft. Try again.');
            setDraft(null);
        } finally {
            setDraftLoading(false);
        }
    };

    const handleDiscardDraft = () => {
        setDraft(null);
        setEditedDraftText('');
        setDraftError(null);
        setSendError(null);
        setSendSuccess(null);
    };

    const handleSend = async () => {
        if (!draft || !selectedJob) return;
        if (!resolvedCompany || !resolvedCompany.owner.userId) {
            setSendError('This job\'s company has no owner on file — cannot send');
            return;
        }
        if (!editedDraftText.trim()) {
            setSendError('Draft text cannot be empty');
            return;
        }
        setSending(true);
        setSendError(null);
        try {
            const recommendations = draft.candidates.map(draftCandidateToRecommendation);
            const res = await sendAsDavidToCompany({
                target_user_id: resolvedCompany.owner.userId,
                target_role: 'company',
                company_id: resolvedCompany.id,
                company_name: resolvedCompany.name,
                content: editedDraftText.trim(),
                candidate_recommendations: recommendations,
                recruiter_query: draft.recruiter_text,
                structured_query: draft.structured_query,
                original_draft: draft.draft,
            });
            setSendSuccess(
                `Sent to ${resolvedCompany.name} (${resolvedCompany.owner.email || 'owner'}). Message ID: ${res.message_id.slice(-8)}`
            );
            setDraft(null);
            setEditedDraftText('');
            setSelectedJobId('');
        } catch (err: any) {
            setSendError(err?.message || 'Failed to send. Check backend logs.');
        } finally {
            setSending(false);
        }
    };

    // ── Render ──────────────────────────────────────────────────────────────

    return (
        <AdminLayout>
            {/* Pin the workspace to the viewport height minus the AdminLayout
                chrome (shared DashboardHeader + page padding ≈ 160px). The
                3-pane layout below needs a bounded parent so each pane's
                overflow-y-auto triggers independent scrolling instead of
                the whole page growing taller than the viewport and clipping
                content behind the header. */}
            <div className="h-[calc(100vh-160px)] flex flex-col bg-gray-50 rounded-xl overflow-hidden border border-gray-200">
                <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between flex-shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                            <Bot className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <h1 className="text-xl font-semibold text-gray-900">David Inbox</h1>
                            <p className="text-sm text-gray-500">
                                Pick a job → David finds candidates → review, edit, and send to the company
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
                        <button
                            type="button"
                            onClick={() => setViewMode('compose')}
                            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === 'compose' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
                        >
                            Compose
                        </button>
                        <button
                            type="button"
                            onClick={() => setViewMode('history')}
                            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === 'history' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
                        >
                            History
                        </button>
                    </div>
                </div>

                {viewMode === 'compose' ? (
                    <ComposeView
                        jobs={filteredJobs}
                        jobsLoading={jobsLoading}
                        jobSearch={jobSearch}
                        setJobSearch={setJobSearch}
                        selectedJob={selectedJob}
                        setSelectedJobId={setSelectedJobId}
                        resolvedCompany={resolvedCompany}
                        resolvingCompany={resolvingCompany}
                        draft={draft}
                        draftLoading={draftLoading}
                        draftError={draftError}
                        editedDraftText={editedDraftText}
                        setEditedDraftText={setEditedDraftText}
                        sending={sending}
                        sendError={sendError}
                        sendSuccess={sendSuccess}
                        onRunDraft={handleRunDraft}
                        onDiscardDraft={handleDiscardDraft}
                        onSend={handleSend}
                    />
                ) : (
                    <HistoryView
                        history={history}
                        historyLoading={historyLoading}
                        activeConversationId={activeConversationId}
                        setActiveConversationId={setActiveConversationId}
                        activeMessages={activeMessages}
                        activeMessagesLoading={activeMessagesLoading}
                    />
                )}
            </div>
        </AdminLayout>
    );
};

// ────────────────────────────────────────────────────────────────────────────
// ComposeView — 3-pane workspace
// ────────────────────────────────────────────────────────────────────────────

interface ComposeViewProps {
    jobs: JobSummary[];
    jobsLoading: boolean;
    jobSearch: string;
    setJobSearch: (v: string) => void;
    selectedJob: JobSummary | undefined;
    setSelectedJobId: (id: string) => void;
    resolvedCompany: CompanyDetail | null;
    resolvingCompany: boolean;
    draft: DraftResponse | null;
    draftLoading: boolean;
    draftError: string | null;
    editedDraftText: string;
    setEditedDraftText: (v: string) => void;
    sending: boolean;
    sendError: string | null;
    sendSuccess: string | null;
    onRunDraft: () => void;
    onDiscardDraft: () => void;
    onSend: () => void;
}

const ComposeView: React.FC<ComposeViewProps> = ({
    jobs,
    jobsLoading,
    jobSearch,
    setJobSearch,
    selectedJob,
    setSelectedJobId,
    resolvedCompany,
    resolvingCompany,
    draft,
    draftLoading,
    draftError,
    editedDraftText,
    setEditedDraftText,
    sending,
    sendError,
    sendSuccess,
    onRunDraft,
    onDiscardDraft,
    onSend,
}) => {
    const [jobDropdownOpen, setJobDropdownOpen] = useState(false);

    return (
        <div className="flex-1 flex overflow-hidden min-h-0">
            {/* LEFT — Job picker + actions */}
            <div className="w-[360px] border-r border-gray-200 bg-white flex flex-col overflow-hidden">
                <div className="p-5 border-b border-gray-100">
                    <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-3">
                        1. Pick a job
                    </h2>
                    <div className="relative">
                        <button
                            type="button"
                            onClick={() => setJobDropdownOpen((v) => !v)}
                            className="w-full flex items-start justify-between gap-2 px-3 py-2.5 border border-gray-300 rounded-lg text-sm text-left hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                            {selectedJob ? (
                                <span className="flex-1 min-w-0">
                                    <span className="block text-gray-900 font-medium truncate">{selectedJob.title}</span>
                                    <span className="block text-xs text-gray-500 truncate">
                                        {selectedJob.companyName}
                                        {selectedJob.location ? ` · ${selectedJob.location}` : ''}
                                    </span>
                                </span>
                            ) : (
                                <span className="text-gray-500">
                                    {jobsLoading ? 'Loading jobs…' : 'Pick a job…'}
                                </span>
                            )}
                            <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                        </button>

                        {jobDropdownOpen && (
                            <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-96 overflow-hidden flex flex-col">
                                <div className="p-2 border-b border-gray-100">
                                    <div className="relative">
                                        <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                                        <input
                                            type="text"
                                            value={jobSearch}
                                            onChange={(e) => setJobSearch(e.target.value)}
                                            placeholder="Search by title, company, location…"
                                            className="w-full pl-8 pr-3 py-1.5 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                        />
                                    </div>
                                </div>
                                <div className="overflow-y-auto flex-1">
                                    {jobs.length === 0 ? (
                                        <p className="px-4 py-3 text-sm text-gray-500 text-center">
                                            {jobsLoading ? 'Loading…' : 'No jobs found'}
                                        </p>
                                    ) : (
                                        jobs.map((j) => (
                                            <button
                                                key={j.id}
                                                type="button"
                                                onClick={() => {
                                                    setSelectedJobId(j.id);
                                                    setJobDropdownOpen(false);
                                                    setJobSearch('');
                                                }}
                                                className="w-full text-left px-4 py-2.5 hover:bg-gray-50 border-b border-gray-50 last:border-0"
                                            >
                                                <div className="text-sm font-medium text-gray-900 truncate">{j.title}</div>
                                                <div className="text-xs text-gray-500 truncate">
                                                    {j.companyName}
                                                    {j.location ? ` · ${j.location}` : ''}
                                                    {j.experienceLevel ? ` · ${j.experienceLevel}` : ''}
                                                </div>
                                            </button>
                                        ))
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Target recap */}
                    {selectedJob && (
                        <div className="mt-3 px-3 py-2 bg-indigo-50 border border-indigo-100 rounded-md text-xs text-indigo-900">
                            <div className="flex items-center gap-1.5 font-medium">
                                <Building2 className="w-3.5 h-3.5" />
                                <span className="truncate">{selectedJob.companyName}</span>
                            </div>
                            {resolvingCompany && (
                                <div className="mt-1 text-indigo-600">Resolving owner…</div>
                            )}
                            {!resolvingCompany && resolvedCompany && (
                                <div className="mt-1 text-indigo-700">
                                    Will send to <span className="font-medium">{resolvedCompany.owner.name || 'owner'}</span>
                                    {resolvedCompany.owner.email && (
                                        <span className="text-indigo-600"> · {resolvedCompany.owner.email}</span>
                                    )}
                                </div>
                            )}
                            {!resolvingCompany && !resolvedCompany && (
                                <div className="mt-1 text-amber-700">Company owner could not be resolved</div>
                            )}
                        </div>
                    )}

                    <button
                        type="button"
                        onClick={onRunDraft}
                        disabled={draftLoading || !selectedJob}
                        className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-lg text-sm font-medium hover:from-indigo-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                        {draftLoading ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                David is thinking…
                            </>
                        ) : (
                            <>
                                <Sparkles className="w-4 h-4" />
                                Find candidates & draft
                            </>
                        )}
                    </button>
                    {draftError && (
                        <p className="mt-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                            {draftError}
                        </p>
                    )}
                </div>

                <div className="flex-1 overflow-y-auto p-5">
                    {sendSuccess && (
                        <div className="flex items-start gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-md">
                            <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                            <p className="text-xs text-green-900">{sendSuccess}</p>
                        </div>
                    )}
                    {sendError && (
                        <div className="mt-2 flex items-start gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-md">
                            <X className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                            <p className="text-xs text-red-900">{sendError}</p>
                        </div>
                    )}
                </div>
            </div>

            {/* MIDDLE — Search workspace */}
            <div className="flex-1 overflow-y-auto bg-gray-50">
                {!draft && !draftLoading && (
                    <div className="h-full flex items-center justify-center">
                        <div className="text-center max-w-md px-6">
                            <div className="w-16 h-16 rounded-2xl bg-white border border-gray-200 flex items-center justify-center mx-auto mb-4">
                                <Sparkles className="w-8 h-8 text-indigo-500" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-1">Your workspace is empty</h3>
                            <p className="text-sm text-gray-500">
                                Pick a job from the panel on the left and click "Find candidates & draft" — David will
                                run hybrid retrieval across the candidate corpus and draft a reply you can edit and send.
                            </p>
                        </div>
                    </div>
                )}

                {draftLoading && (
                    <div className="h-full flex items-center justify-center">
                        <div className="text-center">
                            <Loader2 className="w-8 h-8 animate-spin text-indigo-500 mx-auto mb-3" />
                            <p className="text-sm text-gray-600">Running hybrid retrieval + reranking…</p>
                            <p className="text-xs text-gray-400 mt-1">This usually takes ~100 seconds</p>
                        </div>
                    </div>
                )}

                {draft && (
                    <div className="p-6 space-y-5">
                        <section className="bg-white rounded-xl border border-gray-200 p-5">
                            <div className="flex items-center gap-2 mb-3">
                                <Search className="w-4 h-4 text-gray-500" />
                                <h3 className="text-sm font-semibold text-gray-900">David's interpretation</h3>
                            </div>
                            <p className="text-sm text-gray-700 italic mb-2">"{draft.recruiter_text}"</p>
                            <div className="text-xs text-gray-500">
                                Retrieved <span className="font-semibold text-gray-900">{draft.total_retrieved}</span> candidates · showing top {draft.candidates.length}
                            </div>
                        </section>

                        <section>
                            <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                <User className="w-4 h-4 text-gray-500" />
                                Candidates
                            </h3>
                            <div className="space-y-3">
                                {draft.candidates.map((c, idx) => (
                                    <CandidateCard key={c.id + idx} candidate={c} rank={idx + 1} />
                                ))}
                            </div>
                        </section>
                    </div>
                )}
            </div>

            {/* RIGHT — Editable draft */}
            <div className="w-[420px] border-l border-gray-200 bg-white flex flex-col overflow-hidden">
                <div className="p-5 border-b border-gray-100 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <MessageCircle className="w-4 h-4 text-gray-500" />
                        <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                            2. Edit & send
                        </h2>
                    </div>
                    {draft && (
                        <button
                            type="button"
                            onClick={onDiscardDraft}
                            className="text-xs text-gray-500 hover:text-gray-900 underline"
                        >
                            Discard
                        </button>
                    )}
                </div>

                {!draft ? (
                    <div className="flex-1 flex items-center justify-center text-center px-6">
                        <p className="text-sm text-gray-400">Generate a draft to start editing</p>
                    </div>
                ) : (
                    <>
                        <div className="flex-1 overflow-y-auto p-5 flex flex-col">
                            <label className="text-xs text-gray-500 mb-2">David's draft (editable)</label>
                            <textarea
                                value={editedDraftText}
                                onChange={(e) => setEditedDraftText(e.target.value)}
                                className="flex-1 min-h-[300px] w-full px-3 py-3 border border-gray-300 rounded-lg text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none font-mono"
                            />
                            {editedDraftText !== draft.draft && (
                                <p className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-1.5">
                                    ✎ You've edited David's original draft
                                </p>
                            )}
                        </div>
                        <div className="border-t border-gray-100 p-4 bg-gray-50">
                            <button
                                type="button"
                                onClick={onSend}
                                disabled={sending || !resolvedCompany?.owner.userId}
                                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-indigo-600 text-white rounded-lg text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                {sending ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Sending…
                                    </>
                                ) : (
                                    <>
                                        <Send className="w-4 h-4" />
                                        Send {draft.candidates.length} candidates to {resolvedCompany?.name || '…'}
                                    </>
                                )}
                            </button>
                            {!resolvedCompany?.owner.userId && (
                                <p className="mt-2 text-xs text-gray-500 text-center">
                                    This job's company has no owner on file
                                </p>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

// ────────────────────────────────────────────────────────────────────────────
// CandidateCard
// ────────────────────────────────────────────────────────────────────────────

const SOURCE_LABELS: Record<string, { label: string; color: string }> = {
    platform: { label: 'Platform', color: 'bg-green-100 text-green-800 border-green-200' },
    imported: { label: 'Imported', color: 'bg-blue-100 text-blue-800 border-blue-200' },
    imported_se: { label: 'Imported · SE', color: 'bg-purple-100 text-purple-800 border-purple-200' },
};

const CandidateCard: React.FC<{ candidate: DraftCandidate; rank: number }> = ({ candidate, rank }) => {
    const sourceBadge = candidate.source
        ? SOURCE_LABELS[candidate.source] || { label: candidate.source, color: 'bg-gray-100 text-gray-700 border-gray-200' }
        : null;

    const qwen = typeof candidate.qwen_score === 'number' ? candidate.qwen_score : null;
    const vec = typeof candidate.vector_similarity === 'number' ? candidate.vector_similarity : null;

    return (
        <div className="bg-white rounded-xl border border-gray-200 p-4 hover:border-indigo-300 transition-colors">
            <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
                        {rank}
                    </div>
                    <div className="min-w-0">
                        <h4 className="text-sm font-semibold text-gray-900 truncate">{candidate.name}</h4>
                        {candidate.titles && candidate.titles[0] && (
                            <p className="text-xs text-gray-500 truncate">{candidate.titles[0]}</p>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                    {sourceBadge && (
                        <span className={`px-2 py-0.5 rounded-md border text-[10px] font-semibold uppercase tracking-wide ${sourceBadge.color}`}>
                            {sourceBadge.label}
                        </span>
                    )}
                </div>
            </div>

            <div className="flex items-center gap-3 mb-3 text-xs flex-wrap">
                {qwen !== null && (
                    <div className="flex items-center gap-1">
                        <Star className="w-3 h-3 text-amber-500" />
                        <span className="text-gray-500">Qwen</span>
                        <span className="font-semibold text-gray-900">{qwen.toFixed(1)}</span>
                    </div>
                )}
                {vec !== null && (
                    <div className="flex items-center gap-1">
                        <span className="text-gray-500">Vector</span>
                        <span className="font-semibold text-gray-900">{(vec * 100).toFixed(1)}%</span>
                    </div>
                )}
                {typeof candidate.years_experience === 'number' && candidate.years_experience > 0 && (
                    <div className="flex items-center gap-1">
                        <Briefcase className="w-3 h-3 text-gray-400" />
                        <span className="text-gray-700">{candidate.years_experience}y</span>
                    </div>
                )}
                {candidate.locations && candidate.locations[0] && (
                    <div className="flex items-center gap-1 text-gray-600 truncate">
                        <MapPin className="w-3 h-3 text-gray-400 flex-shrink-0" />
                        <span className="truncate">{candidate.locations[0]}</span>
                    </div>
                )}
            </div>

            {candidate.matched_signals && <MatchedSignals sig={candidate.matched_signals} />}

            {(candidate.companies?.length || candidate.skills?.length) && (
                <div className="mt-3 pt-3 border-t border-gray-100 grid grid-cols-1 gap-1.5 text-xs text-gray-600">
                    {candidate.companies && candidate.companies.length > 0 && (
                        <div className="flex items-start gap-2">
                            <Building2 className="w-3 h-3 text-gray-400 mt-0.5 flex-shrink-0" />
                            <span className="truncate">{candidate.companies.slice(0, 3).join(' · ')}</span>
                        </div>
                    )}
                    {candidate.skills && candidate.skills.length > 0 && (
                        <div className="flex items-start gap-2">
                            <GraduationCap className="w-3 h-3 text-gray-400 mt-0.5 flex-shrink-0" />
                            <span className="truncate">{candidate.skills.slice(0, 8).join(' · ')}</span>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const MatchedSignals: React.FC<{ sig: NonNullable<DraftCandidate['matched_signals']> }> = ({ sig }) => {
    const groups: { label: string; items?: string[]; color: string }[] = [
        { label: 'Skills', items: sig.required_skills_matched, color: 'bg-red-50 text-red-700 border-red-200' },
        { label: 'Titles', items: sig.preferred_titles_matched, color: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
        { label: 'Companies', items: sig.preferred_companies_matched, color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
        { label: 'Keywords', items: sig.keywords_matched, color: 'bg-gray-100 text-gray-700 border-gray-200' },
    ];
    const nonEmpty = groups.filter((g) => g.items && g.items.length > 0);
    if (nonEmpty.length === 0) return null;

    return (
        <div className="space-y-1">
            {nonEmpty.map((g) => (
                <div key={g.label} className="flex items-start gap-2 text-[11px]">
                    <span className="text-gray-500 font-medium w-20 flex-shrink-0 pt-0.5">{g.label}</span>
                    <div className="flex flex-wrap gap-1">
                        {g.items!.map((t) => (
                            <span key={t} className={`px-1.5 py-0.5 rounded border ${g.color}`}>
                                {t}
                            </span>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
};

// ────────────────────────────────────────────────────────────────────────────
// HistoryView
// ────────────────────────────────────────────────────────────────────────────

interface HistoryViewProps {
    history: DavidCompanyConversation[];
    historyLoading: boolean;
    activeConversationId: string | null;
    setActiveConversationId: (id: string | null) => void;
    activeMessages: DavidCompanyMessage[];
    activeMessagesLoading: boolean;
}

const HistoryView: React.FC<HistoryViewProps> = ({
    history,
    historyLoading,
    activeConversationId,
    setActiveConversationId,
    activeMessages,
    activeMessagesLoading,
}) => {
    return (
        <div className="flex-1 flex overflow-hidden min-h-0">
            <div className="w-[360px] border-r border-gray-200 bg-white flex flex-col overflow-hidden">
                <div className="p-4 border-b border-gray-100">
                    <h2 className="text-sm font-semibold text-gray-900">Past conversations</h2>
                    <p className="text-xs text-gray-500 mt-0.5">Sorted by most recent activity</p>
                </div>
                <div className="flex-1 overflow-y-auto">
                    {historyLoading && (
                        <div className="p-6 text-center">
                            <Loader2 className="w-5 h-5 animate-spin text-indigo-500 mx-auto" />
                        </div>
                    )}
                    {!historyLoading && history.length === 0 && (
                        <p className="p-6 text-sm text-gray-500 text-center">No conversations yet</p>
                    )}
                    {history.map((conv) => (
                        <button
                            key={conv.id}
                            type="button"
                            onClick={() => setActiveConversationId(conv.id)}
                            className={`w-full text-left px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors ${activeConversationId === conv.id ? 'bg-indigo-50' : ''}`}
                        >
                            <div className="flex items-center gap-2 mb-1">
                                <Building2 className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
                                <span className="text-sm font-medium text-gray-900 truncate">
                                    {conv.company_name || conv.target_user_id}
                                </span>
                            </div>
                            {conv.last_message && (
                                <p className="text-xs text-gray-500 truncate">{conv.last_message.content}</p>
                            )}
                            <p className="text-[10px] text-gray-400 mt-1">
                                {new Date(conv.updated_at).toLocaleString()}
                            </p>
                        </button>
                    ))}
                </div>
            </div>

            <div className="flex-1 overflow-y-auto bg-gray-50">
                {!activeConversationId && (
                    <div className="h-full flex items-center justify-center">
                        <p className="text-sm text-gray-500">Select a conversation to view messages</p>
                    </div>
                )}
                {activeConversationId && activeMessagesLoading && (
                    <div className="h-full flex items-center justify-center">
                        <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
                    </div>
                )}
                {activeConversationId && !activeMessagesLoading && (
                    <div className="p-6 space-y-4 max-w-3xl mx-auto">
                        {activeMessages.map((msg) => (
                            <div key={msg.id} className="bg-white rounded-xl border border-gray-200 p-5">
                                <div className="flex items-center gap-2 mb-3">
                                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                                        <Bot className="w-3.5 h-3.5 text-white" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-semibold text-gray-900">David</div>
                                        <div className="text-[10px] text-gray-500">
                                            {new Date(msg.created_at).toLocaleString()}
                                        </div>
                                    </div>
                                </div>

                                {msg.recruiter_query && (
                                    <div className="mb-3 px-3 py-2 bg-gray-50 border border-gray-200 rounded-md">
                                        <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-1">Context</p>
                                        <p className="text-xs text-gray-700 italic">"{msg.recruiter_query}"</p>
                                    </div>
                                )}

                                <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
                                    {msg.content}
                                </p>

                                {msg.candidate_recommendations && msg.candidate_recommendations.length > 0 && (
                                    <div className="mt-4 pt-4 border-t border-gray-100">
                                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                                            Suggested candidates ({msg.candidate_recommendations.length})
                                        </p>
                                        <div className="grid grid-cols-1 gap-2">
                                            {msg.candidate_recommendations.map((rec, i) => (
                                                <div key={rec.candidate_id + i} className="px-3 py-2 bg-gray-50 rounded-md border border-gray-100 text-xs">
                                                    <div className="flex items-center justify-between">
                                                        <span className="font-medium text-gray-900 truncate">{rec.name}</span>
                                                        {typeof rec.qwen_score === 'number' && (
                                                            <span className="text-amber-600 font-semibold flex items-center gap-0.5">
                                                                <Star className="w-3 h-3" />
                                                                {rec.qwen_score.toFixed(0)}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {rec.titles && rec.titles[0] && (
                                                        <p className="text-gray-500 truncate">{rec.titles[0]}</p>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default DavidInbox;
